/**
 * @file HowToBecomeABcba.jsx
 * @description Standalone educational/career-intent SEO page at
 *   /how-to-become-a-bcba — explains, at a category level only, the general
 *   BACB pathway to becoming a Board Certified Behavior Analyst (BCBA), then
 *   bridges into how ABAcases helps once someone is certified (or in
 *   process) and ready to find real BCBA-level case work. Deliberately
 *   conservative: no specific numeric requirements (degree types stay
 *   general, no fieldwork-hour counts, CEU counts, exam question/time
 *   counts, fees, or ages), no certification/training/supervision claims by
 *   ABAcases, no active case or provider counts. Links out for real, three
 *   times, to official BACB pages as the authoritative source. Mirrors the
 *   structure of HowToBecomeAnRbt.jsx (hero, disclaimer banner, prose + card
 *   sections, FAQ with JSON-LD, gradient closing CTA, PublicFooter).
 *
 *   Rendered under TopNav (guest nav), so it has no brand header of its own.
 *
 *   i18n: all visible copy is sourced from the "howToBecomeBcba" namespace
 *   via useTranslation(). usePageSeo() (raw SEO meta) and the FAQS constant
 *   fed to useFaqJsonLd() are intentionally left as static English — Google
 *   only indexes the static prerendered English version of meta/schema, so
 *   those stay hardcoded while the rendered JSX pulls from t().
 * @module components/HowToBecomeABcba
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { T } from "../theme";
import {
  ClipboardIcon, UsersIcon, AwardIcon, BuildingIcon, BellIcon,
  InfoIcon, ExternalLinkIcon, StarIcon, UserCheckIcon, UserIcon,
} from "./Icons";
import PublicFooter from "./PublicFooter";
import GuideReviewStamp from "./GuideReviewStamp";
import { GuideAtAGlance, GuideYourPath } from "./GuideShared";
import { setSignupIntent } from "../lib/signupIntent";
import { useFaqJsonLd, useBreadcrumbJsonLd, useSiteSchema } from "../lib/seoSchema";
import { usePageSeo, seoHref } from "../lib/seo";
import { track, A, ctaMeta } from "../lib/analytics";
import Breadcrumbs from "./Breadcrumbs";

const NAVY = "#0F1024";
const NAVY_MID = "#1a1f3c";
const INDIGO = "#4F46E5";
const INDIGO_LT = "#818cf8";
const BORDER = "rgba(226,232,240,0.8)";
const TEXT_MID = "#475569";

const BACB_BCBA_URL = "https://www.bacb.com/bcba/";
const BACB_UPCOMING_CHANGES_URL = "https://www.bacb.com/upcoming-changes/";
const BACB_EXAM_URL = "https://www.bacb.com/examination-information/";

// General-pathway steps, described in categories only — no specific degree
// program lists, fieldwork-hour counts, exam details, or fees. Exact current
// requirements live at bacb.com, not here. Display text sourced via t() from
// the titleKey/bodyKey below; see the JSON block returned by the conversion
// pass for the underlying English/Spanish strings.
const STEPS = [
  { titleKey: "howToBecomeBcba.step1Title", bodyKey: "howToBecomeBcba.step1Body" },
  { titleKey: "howToBecomeBcba.step2Title", bodyKey: "howToBecomeBcba.step2Body" },
  { titleKey: "howToBecomeBcba.step3Title", bodyKey: "howToBecomeBcba.step3Body" },
  { titleKey: "howToBecomeBcba.step4Title", bodyKey: "howToBecomeBcba.step4Body" },
  { titleKey: "howToBecomeBcba.step5Title", bodyKey: "howToBecomeBcba.step5Body" },
];

// What BCBAs actually do day to day — this varies by setting and employer,
// so these are described as general categories, not a fixed job description.
const ROLE_ITEMS = [
  { Icon: ClipboardIcon, titleKey: "howToBecomeBcba.roleItem1Title", bodyKey: "howToBecomeBcba.roleItem1Body" },
  { Icon: UsersIcon, titleKey: "howToBecomeBcba.roleItem2Title", bodyKey: "howToBecomeBcba.roleItem2Body" },
  { Icon: AwardIcon, titleKey: "howToBecomeBcba.roleItem3Title", bodyKey: "howToBecomeBcba.roleItem3Body" },
  { Icon: BuildingIcon, titleKey: "howToBecomeBcba.roleItem4Title", bodyKey: "howToBecomeBcba.roleItem4Body" },
];

// How the credential ecosystem fits together — general relationships only.
const ECOSYSTEM_ITEMS = [
  { Icon: AwardIcon, titleKey: "howToBecomeBcba.ecoItem1Title", bodyKey: "howToBecomeBcba.ecoItem1Body" },
  { Icon: StarIcon, titleKey: "howToBecomeBcba.ecoItem2Title", bodyKey: "howToBecomeBcba.ecoItem2Body" },
  { Icon: UserCheckIcon, titleKey: "howToBecomeBcba.ecoItem3Title", bodyKey: "howToBecomeBcba.ecoItem3Body" },
  { Icon: UserIcon, titleKey: "howToBecomeBcba.ecoItem4Title", bodyKey: "howToBecomeBcba.ecoItem4Body" },
  { Icon: BuildingIcon, titleKey: "howToBecomeBcba.ecoItem5Title", bodyKey: "howToBecomeBcba.ecoItem5Body" },
];

// SINGLE SOURCE OF TRUTH: the visible FAQ cards and the FAQPage JSON-LD both
// derive from the same i18n keys (faq{n}Q / faq{n}A + the link before/after
// keys) via faqJsonLd in-component. FAQ_META carries only per-item structure
// (which items render an inline BACB link), shared by the cards and the JSON-LD.
const FAQ_META = [
  {},
  { externalLink: "bcba" },
  { externalLink: "upcoming" },
  {},
  {}, // faq5: BACB certification vs insurance credentialing (owner 2026-10-23)
];

export default function HowToBecomeABcba({ onNavigate }) {
  const { t, i18n } = useTranslation();
  const [isMobile, setIsMobile] = useState(() => (typeof window !== "undefined" ? window.innerWidth < 760 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Static English — raw prerendered SEO meta stays language-invariant.
  usePageSeo({
    title: "How to Become a BCBA | ABA Career Guide",
    description: "Learn what a BCBA is, the general path toward BCBA certification, what to confirm with the BACB, and how ABAcases helps BCBAs find case opportunities.",
    canonical: "/how-to-become-a-bcba",
  });
  useSiteSchema(); // global Organization + WebSite
  useBreadcrumbJsonLd("bc-jsonld-how-to-become-a-bcba", [
    { name: "Home", url: "/" },
    { name: "How to Become a BCBA", url: "/how-to-become-a-bcba" },
  ]);
  const faqJsonLd = FAQ_META.map((m, i) => {
    const q = t(`howToBecomeBcba.faq${i + 1}Q`);
    let a;
    if (m.externalLink === "bcba") a = `${t('howToBecomeBcba.faq2ABefore')} bacb.com/bcba ${t('howToBecomeBcba.faq2AAfter')}`;
    else if (m.externalLink === "upcoming") a = `${t('howToBecomeBcba.faq3ABefore')} bacb.com/upcoming-changes${t('howToBecomeBcba.faq3AAfter')}`;
    else a = t(`howToBecomeBcba.faq${i + 1}A`);
    return { q, a };
  });
  useFaqJsonLd("faq-jsonld-how-to-become-a-bcba", faqJsonLd);

  // Crawlable internal link (real <a href> so Googlebot follows it; preventDefault
  // keeps SPA nav). Same pattern as HowToBecomeAnRbt / AboutAbaCases.
  const SeoLink = ({ to, style, children }) => (
    <a href={seoHref(to)} onClick={(e) => { e.preventDefault(); onNavigate(to); }} style={{ textDecoration: "none", ...style }}>{children}</a>
  );

  // Real, external, off-domain links to authoritative BACB sources — the only
  // places on this page that intentionally leave abacases.com.
  const BacbLink = ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: INDIGO, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
      {children} <ExternalLinkIcon size={12} color={INDIGO} />
    </a>
  );

  const goJoin = () => {
    track(A.RESOURCE_PAGE_CTA_CLICK, {
      supabaseMeta: { page: "/how-to-become-a-bcba/" },
      ga4Params: ctaMeta({
        pagePath: "/how-to-become-a-bcba/",
        pageType: "resource",
        ctaLabel: "Join ABA case alerts",
        targetPath: "/auth",
        userLanguage: i18n.language,
        audience: "resource",
      }),
    });
    setSignupIntent("alerts");
    onNavigate("auth");
  };

  const sectionPad = isMobile ? "44px 18px" : "64px 48px";
  const maxW = 900;

  return (
    <div style={{ flex: 1, overflowY: "auto", background: "#fff", fontFamily: T.fontBody }}>

      {/* ── Hero ─────────────────────────────────────────────── */}
      <header style={{ background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_MID} 100%)`, padding: isMobile ? "28px 18px 44px" : "32px 48px 64px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", top: -120, right: "10%", width: 420, height: 420, borderRadius: "50%", background: "rgba(79,70,229,0.16)", filter: "blur(90px)", pointerEvents: "none" }} />
        <div style={{ position: "relative", maxWidth: maxW, margin: "0 auto" }}>
          {/* Breadcrumb */}
          <Breadcrumbs items={[
            { label: t('howToBecomeBcba.breadcrumbHome'), onClick: () => onNavigate("home") },
            { label: t('howToBecomeBcba.breadcrumbCurrent') },
          ]} />

          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, marginBottom: 14 }}>
            <span style={{ fontSize: 11.5, fontWeight: 800, color: INDIGO_LT, background: "rgba(255,255,255,0.1)", borderRadius: 7, padding: "4px 10px", letterSpacing: "0.02em" }}>{t('howToBecomeBcba.heroBadgeLabel')}</span>
            <span style={{ fontSize: 12, color: INDIGO_LT, fontWeight: 600 }}>abacases.com</span>
          </div>

          <h1 style={{ fontSize: isMobile ? 27 : 40, fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.14, letterSpacing: "-0.025em", maxWidth: 760 }}>
            {t('howToBecomeBcba.heroTitle')}
          </h1>
          <p style={{ fontSize: isMobile ? 16 : 19, fontWeight: 600, color: "#e2e8f0", lineHeight: 1.4, maxWidth: 700, margin: "0 0 16px" }}>
            {t('howToBecomeBcba.heroSubtitle')}
          </p>
          <p style={{ fontSize: isMobile ? 15 : 16.5, color: "#cbd5e1", lineHeight: 1.65, maxWidth: 640, margin: "0 0 28px" }}>
            {t('howToBecomeBcba.heroDescription')}
          </p>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <button onClick={goJoin} style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: isMobile ? "13px 24px" : "15px 30px", background: `linear-gradient(135deg, ${INDIGO} 0%, #6366F1 100%)`, color: "#fff", border: "none", borderRadius: 12, fontSize: 15, fontWeight: 800, cursor: "pointer", boxShadow: "0 6px 24px rgba(79,70,229,0.4)" }}>
              <BellIcon size={16} color="#fff" /> {t('howToBecomeBcba.ctaJoinAlerts')}
            </button>
          </div>
        </div>
      </header>

      {/* ── Disclaimer banner ────────────────────────────────── */}
      <div style={{ background: "#eef2ff", borderBottom: `1px solid ${BORDER}`, padding: isMobile ? "16px 18px" : "18px 48px" }}>
        <div style={{ maxWidth: maxW, margin: "0 auto", display: "flex", alignItems: "flex-start", gap: 12 }}>
          <InfoIcon size={17} color={INDIGO} style={{ flexShrink: 0, marginTop: 2 }} />
          <span style={{ fontSize: 13.5, color: NAVY, fontWeight: 600, lineHeight: 1.6 }}>
            {t('howToBecomeBcba.disclaimerBefore')} <BacbLink href={BACB_BCBA_URL}>bacb.com/bcba</BacbLink>{t('howToBecomeBcba.disclaimerAfter')}
          </span>
        </div>
      </div>

      {/* ── At a glance ──────────────────────────────────────── */}
      <GuideAtAGlance
        isMobile={isMobile}
        title={t('guideShared.atGlanceTitle')}
        items={[1, 2, 3, 4, 5, 6].map((n) => t(`howToBecomeBcba.atGlanceItem${n}`))}
        confirmPre={t('howToBecomeBcba.atGlanceConfirm')}
        confirmHref={BACB_BCBA_URL}
        confirmLabel="bacb.com/bcba"
      />

      {/* ── 1. What is a BCBA? ───────────────────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeBcba.section1Title')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('howToBecomeBcba.section1Body1')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToBecomeBcba.section1Body2')}
          </p>
        </div>
      </section>

      {/* ── 2. What BCBAs do in ABA services ─────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 16px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeBcba.section2Title')}
          </h2>
          <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.7, marginBottom: 22 }}>
            {t('howToBecomeBcba.section2Intro')}
          </p>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)", gap: 16, marginBottom: 20 }}>
            {ROLE_ITEMS.map((c) => (
              <div key={c.titleKey} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "20px 20px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
                <div style={{ width: 42, height: 42, borderRadius: 11, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <c.Icon size={19} color={INDIGO} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 5 }}>{t(c.titleKey)}</div>
                  <div style={{ fontSize: 13, color: TEXT_MID, lineHeight: 1.6 }}>{t(c.bodyKey)}</div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.7 }}>
            {t('howToBecomeBcba.section2Closing')}
          </p>
        </div>
      </section>

      {/* ── 3. General steps to become a BCBA ────────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 16px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeBcba.section3Title')}
          </h2>
          <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.7, marginBottom: 22 }}>
            {t('howToBecomeBcba.section3Intro')}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 22 }}>
            {STEPS.map((s, i) => (
              <div key={s.titleKey} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "16px 20px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: INDIGO, color: "#fff", fontWeight: 800, fontSize: 13.5, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  {i + 1}
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 4 }}>{t(s.titleKey)}</div>
                  <div style={{ fontSize: 13.5, color: TEXT_MID, lineHeight: 1.6 }}>{t(s.bodyKey)}</div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 15, color: NAVY, fontWeight: 600, lineHeight: 1.7 }}>
            {t('howToBecomeBcba.section3ClosingBefore')} <BacbLink href={BACB_BCBA_URL}>bacb.com/bcba</BacbLink>{t('howToBecomeBcba.section3ClosingAfter')}
          </p>
          <div data-testid="from-rbt-to-bcba" style={{ marginTop: 26, background: "#f5f3ff", border: `1px solid ${BORDER}`, borderLeft: `4px solid ${INDIGO}`, borderRadius: 12, padding: isMobile ? "18px" : "22px 26px" }}>
            <h3 style={{ fontSize: isMobile ? 18 : 20, fontWeight: 900, color: NAVY, margin: "0 0 8px", letterSpacing: "-0.015em" }}>{t('howToBecomeBcba.fromRbtTitle')}</h3>
            <p style={{ fontSize: 15, color: TEXT_MID, lineHeight: 1.75, margin: 0 }}>
              {t('howToBecomeBcba.fromRbtPre')}{" "}
              <SeoLink to="how-to-become-an-rbt" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeBcba.fromRbtLink')}</SeoLink>{t('howToBecomeBcba.fromRbtPost')}
            </p>
          </div>
        </div>
      </section>

      {/* ── 4. What to confirm with the BACB ─────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeBcba.section4Title')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToBecomeBcba.section4Body1Before')} <BacbLink href={BACB_BCBA_URL}>bacb.com/bcba</BacbLink> {t('howToBecomeBcba.section4Body1After')}
          </p>
        </div>
      </section>

      {/* ── 5. BCBA exam and application basics ──────────────── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeBcba.section5Title')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('howToBecomeBcba.section5Body1')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToBecomeBcba.section5Body2Before')} <BacbLink href={BACB_EXAM_URL}>bacb.com/examination-information</BacbLink>{t('howToBecomeBcba.section5Body2After')}
          </p>
        </div>
      </section>

      {/* ── 6. How BCBAs, BCaBAs, RBTs, BTs, and agencies work together ── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 16px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeBcba.section6Title')}
          </h2>
          <p style={{ fontSize: 14.5, color: TEXT_MID, lineHeight: 1.7, marginBottom: 22 }}>
            {t('howToBecomeBcba.section6Intro')}
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12, marginBottom: 22 }}>
            {ECOSYSTEM_ITEMS.map((c) => (
              <div key={c.titleKey} style={{ display: "flex", gap: 14, alignItems: "flex-start", background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 14, padding: "16px 20px", boxShadow: "0 1px 6px rgba(15,16,36,0.04)" }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <c.Icon size={18} color={INDIGO} />
                </div>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 800, color: NAVY, marginBottom: 4 }}>{t(c.titleKey)}</div>
                  <div style={{ fontSize: 13.5, color: TEXT_MID, lineHeight: 1.6 }}>{t(c.bodyKey)}</div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToBecomeBcba.section6ClosingPart1')}{" "}
            <SeoLink to="rbt-vs-bt-vs-bcba-vs-bcaba" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeBcba.section6LinkRbtVsBt')}</SeoLink>{" "}
            {t('howToBecomeBcba.section6ClosingPart2')}{" "}
            <SeoLink to="aba-roles-guide" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeBcba.section6LinkRolesGuide')}</SeoLink>{" "}
            {t('howToBecomeBcba.section6ClosingPart3')}
          </p>
        </div>
      </section>

      {/* ── 7. How ABAcases helps BCBAs find better-fit opportunities ── */}
      <section style={{ padding: sectionPad }}>
        <div style={{ maxWidth: maxW, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 22 : 28, fontWeight: 900, color: NAVY, margin: "0 0 18px", letterSpacing: "-0.02em" }}>
            {t('howToBecomeBcba.section7Title')}
          </h2>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75, marginBottom: 14 }}>
            {t('howToBecomeBcba.section7Body1Part1')}{" "}
            <SeoLink to="aba-case-finder" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeBcba.section7LinkCaseFinder')}</SeoLink>{" "}
            {t('howToBecomeBcba.section7Body1Part2')}{" "}
            <SeoLink to="aba-provider-waitlist" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeBcba.section7LinkCaseAlerts')}</SeoLink>{" "}
            {t('howToBecomeBcba.section7Body1Part3')}{" "}
            <SeoLink to="bcba-jobs-miami-fl" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeBcba.section7LinkMiami')}</SeoLink>{" "}
            {t('howToBecomeBcba.section7Body1Part4')}{" "}
            <SeoLink to="bcba-jobs-charlotte-nc" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeBcba.section7LinkCharlotte')}</SeoLink>
            {t('howToBecomeBcba.section7Body1Part5')}{" "}
            <SeoLink to="aba-payment-tracker" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeBcba.section7LinkTracker')}</SeoLink>{" "}
            {t('howToBecomeBcba.section7Body1Part6')}
          </p>
          <p style={{ fontSize: 15.5, color: TEXT_MID, lineHeight: 1.75 }}>
            {t('howToBecomeBcba.section7Body2Part1')}{" "}
            <SeoLink to="about-aba-cases" style={{ color: INDIGO, fontWeight: 700 }}>{t('howToBecomeBcba.section7LinkAbout')}</SeoLink>{" "}
            {t('howToBecomeBcba.section7Body2Part2')}
          </p>
        </div>
      </section>

      {/* ── 8. FAQ ────────────────────────────────────────────── */}
      <section style={{ background: T.surfaceAlt, borderTop: `1px solid ${BORDER}`, padding: sectionPad }}>
        <div style={{ maxWidth: 720, margin: "0 auto" }}>
          <h2 style={{ fontSize: isMobile ? 23 : 30, fontWeight: 900, color: NAVY, margin: "0 0 24px", letterSpacing: "-0.02em", textAlign: "center" }}>
            {t('howToBecomeBcba.faqTitle')}
          </h2>
          {FAQ_META.map((f, i) => (
            <div key={i} style={{ background: "#fff", border: `1px solid ${BORDER}`, borderRadius: 12, padding: "18px 20px", marginBottom: 10 }}>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: NAVY, marginBottom: 8 }}>{t(`howToBecomeBcba.faq${i + 1}Q`)}</div>
              <div style={{ fontSize: 13.5, color: TEXT_MID, lineHeight: 1.7 }}>
                {f.externalLink === "bcba" ? (
                  <>
                    {t('howToBecomeBcba.faq2ABefore')} <BacbLink href={BACB_BCBA_URL}>bacb.com/bcba</BacbLink> {t('howToBecomeBcba.faq2AAfter')}
                  </>
                ) : f.externalLink === "upcoming" ? (
                  <>
                    {t('howToBecomeBcba.faq3ABefore')} <BacbLink href={BACB_UPCOMING_CHANGES_URL}>bacb.com/upcoming-changes</BacbLink>{t('howToBecomeBcba.faq3AAfter')}
                  </>
                ) : t(`howToBecomeBcba.faq${i + 1}A`)}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Source/verification note ──────────────────────────── */}
      <div style={{ padding: isMobile ? "20px 18px" : "22px 48px", textAlign: "center", background: "#fff" }}>
        <p style={{ fontSize: 12, color: TEXT_MID, lineHeight: 1.6, maxWidth: 640, margin: "0 auto" }}>
          {t('howToBecomeBcba.lastReviewedNote')}
        </p>
      </div>

      <GuideYourPath current="how-to-become-a-bcba" onNavigate={onNavigate} isMobile={isMobile} />
      <GuideReviewStamp slug="how-to-become-a-bcba" />
      <PublicFooter />
    </div>
  );
}
